/**
 * GadgetsServiceModule — 2026-06-08 Phase 3D wire-up for the canonical
 * Q Manual VG35002 gadgets catalog REST surface.
 *
 * Wraps the briefing-room backend's gadgets-library endpoints (Phase 3C,
 * commit 546a8a45a). Lets the SPA list every gadget with compact-shape
 * metadata and fetch a single entry's full data.
 *
 * Routes (per briefing_room_backend/routes/gadgets.py):
 *   GET /api/modules/briefing-room/gadgets             → GadgetsManifest
 *   GET /api/modules/briefing-room/gadgets/{gadget_id} → full gadget JSON | 404
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders().
 */
import { getAuthHeaders } from './authHeaders';

/**
 * Q Manual's 13 fine-grained gadget categories. The SPA's Phase 3E 3-tab
 * drawer clusters them into 3 super-categories — Common / Security / Exotic.
 */
export type GadgetCategory =
  | 'attache_case'
  | 'belt'
  | 'pen'
  | 'shoe'
  | 'umbrella'
  | 'watch'
  | 'miscellaneous_common_device'
  | 'search_id_device'
  | 'surveillance_device'
  | 'counter_surveillance_device'
  | 'sensor_detector'
  | 'tracer'
  | 'exotic_device';

export interface GadgetManifestEntry {
  id: string;
  name: string;
  category: GadgetCategory;
  manifest_section: string | null;
  page: number | null;
}

export interface GadgetsManifest {
  gadgets: GadgetManifestEntry[];
  total: number;
  categories_complete: string[];
  categories_pending: string[];
  schema_version: string;
  source: string;
}

const BASE_PATH = '/api/modules/briefing-room/gadgets';

export class GadgetsServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  async list(): Promise<GadgetsManifest> {
    const resp = await this.fetchFn(BASE_PATH, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(`GadgetsServiceModule: list HTTP ${resp.status}`);
    }
    return (await resp.json()) as GadgetsManifest;
  }

  async get(gadgetId: string): Promise<Record<string, unknown> | null> {
    const resp = await this.fetchFn(
      `${BASE_PATH}/${encodeURIComponent(gadgetId)}`,
      {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      },
    );
    if (resp.status === 404) {
      return null;
    }
    if (!resp.ok) {
      throw new Error(`GadgetsServiceModule: get HTTP ${resp.status}`);
    }
    return (await resp.json()) as Record<string, unknown>;
  }
}
